/// <amd-module name="Search/WorkItem/Flux/Sources/TelemetrySpy" />

import * as BaseActionsHub from "Search/Shared/Base/ActionsHub";
import { ActionsHub } from "Search/WorkItem/Flux/ActionsHub";
import { StoresHub } from "Search/WorkItem/Flux/StoresHub";
import { WorkItemSearchRequest, WorkItemSearchResponse, WorkItemResult } from "Search/LegacyWebApi/Contracts/WorkItem";
import { TelemetryWriter } from "Search/WorkItem/Flux/Sources/TelemetryWriter";
import { CustomerIntelligenceConstants, Ref } from "Search/WorkItem/Constants";
import { getFieldValue } from "Search/WorkItem/Utils";

/**
 * Writes telemetry events based on actions in work item search page.
 */
export class TelemetrySpy {
    constructor(
        private telemetryWriter: TelemetryWriter,
        private actionsHub: ActionsHub,
        private storesHub: StoresHub) {
        this.registerActionsHubHandlers();
    }

    public dispose = (): void => {
        this.disposeActionsHubHandlers();
        this.telemetryWriter.dispose();
    }

    private registerActionsHubHandlers = (): void => {
        this.actionsHub.searchStarted.addListener(this.publishSearchStarted);
        this.actionsHub.pageInitializationStarted.addListener(this.publishPageInitializationStarted);
        this.actionsHub.pageInitializedWithSeedData.addListener(this.publishLaunchPointTelemetry);
        this.actionsHub.resultsLoaded.addListener(this.publishResultsLoaded);
        this.actionsHub.itemChanged.addListener(this.publishItemChanged);
        this.actionsHub.sortOptionChanged.addListener(this.publishSortOptionChanged);
        this.actionsHub.previewOrientationChanged.addListener(this.publishPreviewOrientationChanged);
    }

    private disposeActionsHubHandlers = (): void => {
        this.actionsHub.searchStarted.removeListener(this.publishSearchStarted);
        this.actionsHub.pageInitializationStarted.removeListener(this.publishPageInitializationStarted);
        this.actionsHub.pageInitializedWithSeedData.removeListener(this.publishLaunchPointTelemetry);
        this.actionsHub.resultsLoaded.removeListener(this.publishResultsLoaded);
        this.actionsHub.itemChanged.removeListener(this.publishItemChanged);
        this.actionsHub.sortOptionChanged.removeListener(this.publishSortOptionChanged);
        this.actionsHub.previewOrientationChanged.removeListener(this.publishPreviewOrientationChanged);
    }

    private publishPageInitializationStarted = (payload: BaseActionsHub.IPageInitializationStartedPayload<WorkItemSearchRequest>): void => {
        this.telemetryWriter.notifySearchStarted();
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SearchStarted, {});
        }, 0);
    }

    private publishLaunchPointTelemetry = (payload: BaseActionsHub.IPageInitializedWithSeedDataPayload<WorkItemSearchResponse>): void => {
        setTimeout(() => {
            const { launchPoint } = payload;
            if (launchPoint) {
                this.telemetryWriter.publish(CustomerIntelligenceConstants.LaunchPoint, { launchPoint });
            }
        }, 0);
    }

    private publishSearchStarted = (): void => {
        this.telemetryWriter.notifySearchStarted();
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SearchStarted, {});
        }, 0);
    }

    private publishResultsLoaded = (payload: BaseActionsHub.IResultsLoadedPayload<WorkItemSearchResponse, WorkItemResult>): void => {
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.ResultsLoaded, {
                totalCount: payload.response.results.count,
                resultsShown: payload.response.results.values.length,
                searchId: payload.activityId,
                defaultWitId: payload.response.results.count > 0 ? getFieldValue(payload.activeItem.fields, Ref.id) : -1,
                defaultIndex: 0
            });
        }, 0);
    }

    private publishItemChanged = (payload: BaseActionsHub.IItemChangedPayload<WorkItemResult>): void => {
        setTimeout(() => {
            const { searchStoreState } = this.storesHub.getAggregatedState();
            const { response, activityId } = searchStoreState;
            if (response) {
                this.telemetryWriter.publish(CustomerIntelligenceConstants.ItemChanged, {
                    index: response.results.values.indexOf(payload.item),
                    searchId: activityId,
                    WitId: getFieldValue(payload.item.fields, Ref.id)
                });
            }
        }, 0);
    }

    private publishSortOptionChanged = (payload: BaseActionsHub.ISortOptionChangedPayload<WorkItemResult>): void => {
        setTimeout(() => {
            const { sortOption } = payload;
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SortOptionChanged, { ...sortOption });
        }, 0);
    }

    private publishPreviewOrientationChanged = (payload: BaseActionsHub.IPreviewOrientationChangedPayload): void => {
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.PreviewOrientationChanged, {
                currentOrientation: payload.previewOrientation.name
            });
        }, 0)
    }
}
